/**
 * Thrown when the direct dynamical.org path cannot serve a query (source not
 * hosted, init time missing from the store, site outside grid coverage,
 * unsupported time aggregation, …). Callers fall back to the HF API, which
 * remains the source of truth.
 */
export class DynamicalUnavailableError extends Error {}

/**
 * How each engine words a chunk it couldn't fetch or parse. Chromium and Firefox
 * name the dynamic import; WebKit doesn't. Both complain about the MIME type
 * instead when the server answers a missing asset with the SPA's index.html, and
 * Vite's preload helper has its own wording for the stylesheet case.
 */
const STALE_MODULE_MESSAGE =
  /dynamically imported module|importing a module script failed|javascript module script|failed to load module script|unable to preload/i;

/** Bounds the cause walk in case a chain is circular. */
const MAX_CAUSE_DEPTH = 8;

/**
 * Rethrows lazy-chunk load failures as unavailability: zarrita imports its
 * codecs (e.g. numcodecs' blosc) on demand, and a session that predates the
 * latest deploy can no longer fetch the old hashed asset. The session simply
 * can't serve direct reads until reload — the designed API fallback, not a
 * correctness defect. The failure can arrive wrapped, so the cause chain is
 * checked too.
 */
export function rethrowStaleModuleAsUnavailable(error: unknown): never {
  let cause: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && cause instanceof Error; depth += 1) {
    if (STALE_MODULE_MESSAGE.test(cause.message)) {
      throw new DynamicalUnavailableError(cause.message);
    }
    cause = cause.cause;
  }
  throw error;
}
